import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Outlet } from "react-router-dom";
import { ShieldOff } from "lucide-react";
import { http, formatError } from "@/lib/api";
import { StaffCtx, useStaff } from "@/lib/staff";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { StaffSidebar } from "@/components/staff/StaffSidebar";
import { StaffTopBar } from "@/components/staff/StaffTopBar";
import { ErrorBox } from "@/components/staff/ui";
import { useStaffVerify } from "@/components/staff/VerifyIdentity";
import { useSwipeMenu } from "@/lib/swipeMenu";

const Full = ({ children, testid }) => (
  <div className="min-h-[100dvh] bg-[#0A0F1C] text-neutral-300 flex items-center justify-center px-6 text-sm" data-testid={testid}>{children}</div>
);

export function AccessDenied({ inline }) {
  const body = (
    <div className="max-w-md text-center" data-testid="staff-access-denied">
      <ShieldOff className="h-10 w-10 text-red-300 mx-auto" aria-hidden="true" />
      <h1 className="font-display text-2xl font-semibold text-white mt-4">Access denied</h1>
      <p className="text-sm text-neutral-400 mt-2">
        {inline ? "Your staff role doesn't include this page." : "This area is only for BrynAI staff. If you think you should have access, ask the owner."}
      </p>
      <Link to={inline ? "/staff" : "/app"} className="inline-block mt-5 text-sm text-sky-300 hover:underline" data-testid="staff-access-denied-back">
        {inline ? "Back to the staff overview" : "Back to BrynAI"}
      </Link>
    </div>
  );
  return inline ? <div className="py-16 flex justify-center">{body}</div> : <Full>{body}</Full>;
}

export function Gate({ perm, children }) {
  const { can } = useStaff();
  return can(perm) ? children : <AccessDenied inline />;
}

function StaffShell({ me }) {
  const { getToken, dialog } = useStaffVerify(me);
  const { open: menu, setOpen: setMenu, contentRef, overlayRef } = useSwipeMenu("(max-width: 1023px)");
  const value = useMemo(() => ({ me, can: (p) => me.permissions.includes(p), verify: { getToken } }), [me, getToken]);
  return (
    <StaffCtx.Provider value={value}>
      <div className="h-[100dvh] flex bg-[#0A0F1C] text-neutral-200" data-testid="staff-area">
        <aside className="hidden lg:flex w-[236px] shrink-0"><StaffSidebar /></aside>
        <Sheet open={menu} onOpenChange={setMenu}>
          <SheetContent ref={contentRef} overlayRef={overlayRef} side="left" className="lg:hidden p-0 w-[264px] bg-[#0C1322] border-[#1B2740]" data-testid="staff-mobile-menu">
            <SheetTitle className="sr-only">Staff menu</SheetTitle>
            <StaffSidebar onPick={() => setMenu(false)} />
          </SheetContent>
        </Sheet>
        <div className="flex-1 min-w-0 flex flex-col">
          <StaffTopBar onMenu={() => setMenu(true)} />
          <main className="flex-1 overflow-y-auto scroll-thin">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-10"><Outlet /></div>
          </main>
        </div>
      </div>
      {dialog}
    </StaffCtx.Provider>
  );
}

// The server decides who is staff: anyone else gets "Access denied", whatever link they followed.
export default function StaffLayout() {
  const [state, setState] = useState({ me: undefined, error: "" });
  const load = useCallback(() => {
    setState({ me: undefined, error: "" });
    http.get("/staff/me").then((r) => setState({ me: r.data, error: "" }))
      .catch((e) => setState(e.response?.status === 403 ? { me: null, error: "" } : { me: undefined, error: formatError(e) }));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (state.error) return <Full><ErrorBox error={`Couldn't open the staff area: ${state.error}`} onRetry={load} /></Full>;
  if (state.me === undefined) return <Full testid="staff-loading-screen">Opening the staff area…</Full>;
  if (state.me === null) return <AccessDenied />;
  return <StaffShell me={state.me} />;
}
